<template>
  <div>
    <v-dialog
      v-model="visible"
      max-width="700"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      persistent
      scrollable
      class="screenshot-ticket-dialog"
    >
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">{{ lifebuoyIcon }}</v-icon>
          Request Support
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <!-- Main preview -->
              <v-col cols="12" v-if="activePreviewSrc">
                <v-img
                  :src="activePreviewSrc"
                  max-height="220"
                  contain
                  class="grey lighten-3 mb-2 elevation-2"
                  style="border-radius: 4px; cursor: pointer;"
                  @click="openPreview(activePreviewSrc)"
                />
                <div class="caption grey--text text-center mb-2">
                  Click image to open full size in new tab
                </div>
              </v-col>

              <!-- Thumbnail strip + add buttons -->
              <v-col cols="12">
                <div class="d-flex align-center flex-wrap thumb-strip">
                  <div
                    v-for="(item, i) in allItems"
                    :key="'item-' + i"
                    style="position: relative; cursor: pointer;"
                    @click="selectedIndex = i"
                  >
                    <v-img
                      v-if="item.src"
                      :src="item.src"
                      width="80"
                      height="56"
                      contain
                      class="elevation-1"
                      :class="selectedIndex === i ? 'selected-thumb' : 'grey lighten-3'"
                      style="border-radius: 4px;"
                    />
                    <div v-else class="file-thumb elevation-1 d-flex align-center justify-center"
                      :class="{ 'selected-thumb': selectedIndex === i }">
                      <v-icon small>{{ fileIcon }}</v-icon>
                    </div>
                    <v-btn
                      icon
                      x-small
                      class="thumb-remove"
                      @click.stop="removeItem(i)"
                      :disabled="submitting || !!successMessage"
                    >
                      <v-icon x-small color="white">{{ closeIcon }}</v-icon>
                    </v-btn>
                    <div class="caption text-center text-truncate thumb-label" :title="item.label">
                      {{ item.label }}
                    </div>
                  </div>

                  <v-btn
                    small
                    outlined
                    color="primary"
                    @click="requestCapture"
                    :disabled="submitting || !!successMessage"
                  >
                    <v-icon left small>{{ cameraIcon }}</v-icon>
                    Add Another Screenshot
                  </v-btn>
                  <v-btn small outlined @click="triggerFilePicker" :disabled="submitting || !!successMessage">
                    <v-icon left small>{{ attachIcon }}</v-icon>
                    Attach File
                  </v-btn>
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    hidden
                    @change="onFilesSelected"
                  />
                </div>
              </v-col>

              <v-col cols="12" v-if="capturedPages.length">
                <v-chip
                  v-for="(page, i) in capturedPages"
                  :key="'page-' + i"
                  small
                  outlined
                  color="grey darken-1"
                  class="mb-2 mr-2"
                >
                  <v-icon left small>{{ monitorIcon }}</v-icon>
                  Captured on: {{ page }}
                </v-chip>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="subject"
                  label="Subject"
                  outlined
                  dense
                  :rules="subjectRules"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="description"
                  label="Description"
                  outlined
                  dense
                  rows="3"
                  placeholder="What were you doing, and what happened?"
                  :rules="descriptionRules"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="priority"
                  :items="priorities"
                  label="Priority"
                  outlined
                  dense
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="category"
                  :items="categoryItems"
                  label="Category"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
          </v-container>

          <v-alert v-if="errorMessage" type="error" dense class="mt-2">
            {{ errorMessage }}
          </v-alert>

          <v-alert v-if="successMessage" type="success" dense class="mt-2">
            <div>{{ successMessage }}</div>
            <div class="caption">
              TM Support will follow up with you. You can close this window.
            </div>
            <div v-if="popupBlocked" class="caption">
              Your browser blocked the new tab — open {{ ticketUrl }} to view it.
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions class="flex-wrap">
          <span v-if="submitBlockedReason" class="caption grey--text ml-2 submit-hint">
            {{ submitBlockedReason }}
          </span>
          <v-spacer />
          <template v-if="successMessage">
            <v-btn v-if="ticketUrl" text color="primary" @click="openTicket">
              Open ticket
            </v-btn>
            <v-btn color="primary" @click="discard">Close</v-btn>
          </template>
          <template v-else>
            <v-btn text @click="close" :disabled="submitting">Cancel</v-btn>
            <v-btn
              color="primary"
              @click="submit"
              :loading="submitting"
              :disabled="!canSubmit || submitting"
            >
              <v-icon left>{{ sendIcon }}</v-icon>
              Submit
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDiscard" max-width="380" persistent>
      <v-card>
        <v-card-title class="subtitle-1">Discard this support request?</v-card-title>
        <v-card-text>
          Your screenshots and everything you have typed will be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDiscard = false">Keep editing</v-btn>
          <v-btn text color="error" @click="discard">Discard</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiLifebuoy,
  mdiSend,
  mdiMonitor,
  mdiClose,
  mdiCamera,
  mdiPaperclip,
  mdiFile,
} from '@mdi/js';
import { configUrls } from '@/util/clients/client-config';
import { clients } from '@/util/clients';
import { DEFAULT_CATEGORY, FALLBACK_CATEGORIES, categoryForRoute } from './ticket-categories';
import { toLocalDateTime } from '../../util/shared/vue-global';

const { backendRest } = clients.direct;

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB per file
const MAX_TOTAL_UPLOAD = 40 * 1024 * 1024; // 40 MB for the whole ticket
const SUBMIT_TIMEOUT_MS = 60 * 1000;
const MB = 1024 * 1024;

let cachedCategories = null;

export default {
  name: 'ScreenshotTicketDialog',
  props: {
    value: Boolean,
    screenshots: {
      type: Array,
      default: () => [],
    },
    // Changes when a brand-new ticket is started; that — not merely reopening
    // the dialog — is what clears the form.
    sessionId: {
      type: Number,
      default: 0,
    },
    currentRoute: String,
    currentUser: String,
    currentCounty: String,
    workstation: [String, Number],
  },
  data() {
    return {
      lifebuoyIcon: mdiLifebuoy,
      sendIcon: mdiSend,
      monitorIcon: mdiMonitor,
      closeIcon: mdiClose,
      cameraIcon: mdiCamera,
      attachIcon: mdiPaperclip,
      fileIcon: mdiFile,
      subject: '',
      description: '',
      priority: 'MEDIUM',
      category: DEFAULT_CATEGORY,
      submitting: false,
      confirmDiscard: false,
      errorMessage: '',
      successMessage: '',
      ticketUrl: '',
      popupBlocked: false,
      shownSessionId: null,
      categoryItems: FALLBACK_CATEGORIES,
      selectedIndex: 0,
      attachedFiles: [],
      filePreviews: [],
      subjectRules: [(v) => !!(v && v.trim()) || 'Subject is required'],
      descriptionRules: [(v) => !!(v && v.trim()) || 'Please describe what happened'],
      priorities: [
        { text: 'Low', value: 'LOW' },
        { text: 'Medium', value: 'MEDIUM' },
        { text: 'High', value: 'HIGH' },
        { text: 'Urgent', value: 'URGENT' },
      ],
    };
  },
  computed: {
    visible: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
    allItems() {
      const items = [];
      this.screenshots.forEach((shot, i) => {
        items.push({
          type: 'screenshot',
          src: shot.previewUrl,
          label: `Screenshot ${i + 1}`,
          index: i,
        });
      });
      this.attachedFiles.forEach((file, i) => {
        const isImage = file.type && file.type.startsWith('image/');
        items.push({
          type: 'file',
          src: isImage ? this.filePreviews[i] || null : null,
          label: file.name,
          fileIndex: i,
        });
      });
      return items;
    },
    activePreviewSrc() {
      const item = this.allItems[this.selectedIndex];
      return item ? item.src : null;
    },
    // Distinct screens the screenshots came from, in capture order.
    capturedPages() {
      const pages = this.screenshots
        .map((shot) => shot.route)
        .filter(Boolean);
      if (!pages.length) return this.currentRoute ? [this.currentRoute] : [];
      return pages.filter((page, i) => pages.indexOf(page) === i);
    },
    totalUploadBytes() {
      const shotSize = (sum, shot) => sum + ((shot.blob && shot.blob.size) || 0);
      const shots = this.screenshots.reduce(shotSize, 0);
      return this.attachedFiles.reduce((sum, file) => sum + (file.size || 0), shots);
    },
    hasContent() {
      return !!(this.subject.trim() || this.description.trim()
        || this.attachedFiles.length || this.screenshots.length);
    },
    canSubmit() {
      return !this.submitBlockedReason;
    },
    // Doubles as the on-screen explanation of why Submit is greyed out — a
    // disabled button fires no events, so without this the user is stuck
    // guessing what is missing.
    submitBlockedReason() {
      if (this.successMessage) return '';
      if (this.allItems.length === 0) return 'Attach at least one screenshot or file';
      if (!this.subject.trim()) return 'Add a subject to submit';
      if (!this.description.trim()) return 'Describe what happened to submit';
      if (this.totalUploadBytes > MAX_TOTAL_UPLOAD) {
        return `Attachments total ${this.formatMb(this.totalUploadBytes)} — the limit is `
          + `${this.formatMb(MAX_TOTAL_UPLOAD)}. Remove one and try again.`;
      }
      return '';
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        // Reset here, while the dialog is still invisible, if this is a session
        // we have not shown yet. Covers any future path that reopens it.
        if (this.shownSessionId !== this.sessionId) {
          this.resetForm();
          this.shownSessionId = this.sessionId;
        }
        this.errorMessage = '';
        this.fetchCategories();
      }
    },
    sessionId() {
      // A new ticket can only start while the dialog is closed (onFabClick
      // refuses to run when it is open), so resetting here is never visible.
      if (!this.value) this.resetForm();
    },
    screenshots() {
      this.clampSelectedIndex();
    },
  },
  beforeDestroy() {
    this.cleanupPreviews();
  },
  methods: {
    formatMb(bytes) {
      return `${(bytes / MB).toFixed(1)} MB`;
    },
    resetForm() {
      this.subject = '';
      this.description = '';
      this.priority = 'MEDIUM';
      this.category = categoryForRoute(this.currentRoute);
      this.errorMessage = '';
      this.successMessage = '';
      this.ticketUrl = '';
      this.popupBlocked = false;
      this.selectedIndex = 0;
      this.attachedFiles = [];
      this.cleanupPreviews();
      this.filePreviews = [];
    },
    clampSelectedIndex() {
      if (this.selectedIndex >= this.allItems.length) {
        this.selectedIndex = Math.max(0, this.allItems.length - 1);
      }
    },
    cleanupPreviews() {
      this.filePreviews.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    },
    openPreview(src) {
      if (!src) return;
      const w = window.open();
      if (w) {
        w.document.write(`<img src="${src}" style="max-width:100%;height:auto">`);
        w.document.title = 'Preview';
      }
    },
    openTicket() {
      if (!this.ticketUrl) return;
      // A blocked popup is not a failure — the ticket exists. Show the address
      // in the success alert rather than a red error under a green one.
      this.popupBlocked = !window.open(this.ticketUrl, '_blank');
    },
    requestCapture() {
      this.$emit('capture-more');
    },
    triggerFilePicker() {
      this.$refs.fileInput.click();
    },
    close() {
      if (this.hasContent) {
        this.confirmDiscard = true;
        return;
      }
      this.discard();
    },
    discard() {
      // Deliberately does NOT clear the form: the card stays mounted through
      // the close transition, so blanking it here flashes an empty form on the
      // way out. resetForm() runs on the next open instead (see the value
      // watcher), which also revokes the preview URLs.
      this.confirmDiscard = false;
      this.visible = false;
    },
    removeItem(index) {
      const item = this.allItems[index];
      if (item.type === 'screenshot') {
        this.$emit('remove-screenshot', item.index);
      } else {
        const fi = item.fileIndex;
        if (this.filePreviews[fi]) {
          URL.revokeObjectURL(this.filePreviews[fi]);
        }
        this.attachedFiles.splice(fi, 1);
        this.filePreviews.splice(fi, 1);
      }
      this.clampSelectedIndex();
    },
    onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      const rejected = [];
      files.forEach((file) => {
        if (file.size > MAX_FILE_SIZE) {
          rejected.push(file.name);
          return;
        }
        this.attachedFiles.push(file);
        if (file.type && file.type.startsWith('image/')) {
          this.filePreviews.push(URL.createObjectURL(file));
        } else {
          this.filePreviews.push(null);
        }
      });
      if (rejected.length > 0) {
        this.errorMessage = `Skipped (over 25 MB): ${rejected.join(', ')}`;
      }
      if (this.allItems.length > 0) {
        this.selectedIndex = this.allItems.length - 1;
      }
      const input = event.target;
      input.value = '';
    },
    async fetchCategories() {
      if (cachedCategories) {
        this.categoryItems = cachedCategories;
        return;
      }
      try {
        const urls = configUrls();
        const baseUrl = urls.BACKEND_REST || '/api';
        const response = await fetch(`${baseUrl}/support/categories`);
        if (!response.ok) return;
        const data = await response.json();
        if (data.categories && data.categories.length > 0) {
          cachedCategories = this.buildCategoryItems(data.categories);
          this.categoryItems = cachedCategories;
        }
      } catch (e) {
        // Non-fatal: categoryItems still holds FALLBACK_CATEGORIES.
        console.warn('Support category fetch failed, using built-in list:', e);
      }
    },
    buildCategoryItems(tree) {
      const items = [];
      tree.forEach((parent, index) => {
        if (index > 0) {
          items.push({ divider: true });
        }
        if (parent.children && parent.children.length > 0) {
          items.push({ header: parent.name });
          parent.children.forEach((child) => {
            items.push({
              text: child.name,
              value: `${parent.name} > ${child.name}`,
            });
          });
        } else {
          items.push({
            text: parent.name,
            value: parent.name,
          });
        }
      });
      return items;
    },
    async submit() {
      if (this.submitting || !this.canSubmit) return;
      this.submitting = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const formData = new FormData();
        formData.append('title', this.subject.trim());
        formData.append('description', this.buildDescription());
        formData.append('priority', this.priority);
        if (this.currentUser) formData.append('contactName', this.currentUser);
        if (this.currentCounty) formData.append('county', this.currentCounty);

        // Append screenshot blobs directly — no base64 conversion needed
        const ts = new Date().toISOString().replace(/[:.]/g, '-');
        this.screenshots.forEach((shot, i) => {
          formData.append('files', shot.blob, `screenshot-${i + 1}-${ts}.jpg`);
        });

        // Append attached files
        this.attachedFiles.forEach((file) => {
          formData.append('files', file, file.name);
        });

        const response = await backendRest.post('/support/create-ticket', formData, {
          timeout: SUBMIT_TIMEOUT_MS,
        });
        const result = response.data;
        this.ticketUrl = result.ticketUrl || '';
        this.successMessage = result.ticketNumber
          ? `Ticket ${result.ticketNumber} created.`
          : 'Your support request was sent.';
      } catch (error) {
        this.errorMessage = this.describeError(error);
      } finally {
        this.submitting = false;
      }
    },
    describeError(error) {
      if (error.code === 'ECONNABORTED') {
        return 'The request timed out. Check with support before sending it again, '
          + 'in case the ticket was created.';
      }
      const apiMsg = error.response && error.response.data && error.response.data.message;
      return apiMsg || error.message || 'Failed to create ticket. Please try again.';
    },
    buildDescription() {
      let desc = this.description || '';
      desc += '\n\n--- Auto-captured metadata ---';
      desc += `\nPage: ${this.currentRoute || 'Unknown'}`;
      if (this.screenshots.length > 1) {
        this.screenshots.forEach((shot, i) => {
          desc += `\n  Screenshot ${i + 1}: ${shot.route || 'Unknown'}`;
        });
      }
      desc += `\nCategory: ${this.category}`;
      desc += `\nCounty: ${this.currentCounty || 'N/A'}`;
      desc += `\nUser: ${this.currentUser || 'N/A'}`;
      desc += `\nWorkstation: ${this.workstation || 'N/A'}`;
      desc += `\nBrowser: ${navigator.userAgent}`;
      desc += `\nTimestamp: ${toLocalDateTime(new Date())} Central`;
      desc += `\nScreen: ${window.innerWidth}x${window.innerHeight}`;
      desc += `\nAttachments: ${this.allItems.length}`;
      return desc.trim();
    },
  },
};
</script>

<style scoped>
.submit-hint {
  white-space: normal;
  max-width: 100%;
}
.thumb-strip {
  /* Room for the remove button, which overhangs the thumbnail by 6px. */
  gap: 12px;
}
.thumb-label {
  max-width: 80px;
}
.selected-thumb {
  outline: 2px solid #1976D2;
  outline-offset: 1px;
}
.file-thumb {
  width: 80px;
  height: 56px;
  border-radius: 4px;
  background: #f5f5f5;
}
.thumb-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  background: rgba(0,0,0,0.6) !important;
  width: 18px !important;
  height: 18px !important;
}
</style>
